<!-- RadarChart.svelte -->

<script>

  import { scaleLinear } from "d3-scale";
  import { max } from "d3-array";
  import { lineRadial, curveLinearClosed } from "d3-shape";
  import { select } from "d3-selection";
  import "d3-transition";

  const defaultIcons = { // Está aquí para facilitar usar el componente por sí solo.
    "Long term benefit": "/Icons/Growth icon.png",
    "Trascendence": "/Icons/Star icon.png",
    "Impact on relationships": "/Icons/Heart icon.svg",
    "Cost": "/Icons/Cost icon.svg",
    "Risk": "/Icons/Risk icon.svg",
    "default": "/Icons/Custom Criteria Icon - Signpost base.svg",
  };
	
	let { categories = [], paths = [], iconMapping = defaultIcons, lineWeights = null } = $props();

  let svgElement;
  let tooltip;

  let width  = $state(1);
  let height = $state(1);
  const labelPadding = 30;

  /* ---------- RESIZE ---------- */
  function updateDimensions() {
    const rect = svgElement.getBoundingClientRect();
    width = rect.width;
    height = rect.width;
    createRadarChart();
  }

  /* ---------- CHART DRAW ---------- */
  function createRadarChart() {
    const radius    = Math.min(width, height) / 3;
    const iconSize  = radius * 0.2;
    const svg       = select(svgElement);

    svg.selectAll("*").remove();

    const mainGroup = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const angleScale = scaleLinear()
      .domain([0, categories.length])
      .range([0, 2 * Math.PI]);

    const rawMax = max(paths, (p) => max(p.values));
    // const roundedMax = Math.ceil(rawMax || 1);
    const radiusScale = scaleLinear().domain([0, rawMax]).range([0, radius]);

    const radarLine = lineRadial()
      .curve(curveLinearClosed)
      .radius((d) => radiusScale(d))
      .angle((d, i) => (i * 2 * Math.PI) / categories.length);

    /* grid */
    mainGroup
      .selectAll(".grid")
      .data([rawMax])
      .enter()
      .append("path")
      .attr("class", "grid")
      .attr("d", (d) => radarLine(Array(categories.length).fill(d)))
      .attr("fill", "none")
      .attr("stroke", "#ccc")
      .attr("stroke-opacity", 0.3);

    /* axes */
    mainGroup
      .selectAll(".axis-line")
      .data(categories)
      .enter()
      .append("line")
      .attr("class", "axis-line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", (_d, i) => radiusScale(rawMax) * Math.cos(angleScale(i) - Math.PI / 2))
      .attr("y2", (_d, i) => radiusScale(rawMax) * Math.sin(angleScale(i) - Math.PI / 2))
      .attr("stroke", "#ccc")
      .attr("stroke-opacity", 0.3)
      .attr("stroke-width", 1);

    /* paths */
    paths.forEach((path, pathIndex) => {
      // Use lineWeights if provided, otherwise default to 2
      const strokeWidth = lineWeights ? (lineWeights[pathIndex] ?? 2) : 2;
      const radarPath = mainGroup
        .append("path")
        .datum(path.values)
        .attr("d", radarLine)
        .attr("fill", path.color)
        .attr("stroke", path.color)
        .attr("stroke-width", strokeWidth)
        .attr("fill-opacity", 0.05)
        .on("mouseover touchstart", function () {
          select(this).transition().duration(150).attr("fill-opacity", 0.6);
          tooltip
            .style("display", "block")
            .html(`${path.name}`) // area tooltip only name
            .style("left", `${event.pageX + 5}px`)
            .style("top", `${event.pageY + 5}px`);
        })
        .on("mousemove touchmove", function () {
          tooltip
            .style("left", `${event.pageX + 5}px`)
            .style("top", `${event.pageY + 5}px`);
        })
        .on("mouseout touchend", function () {
          select(this).transition().duration(150).attr("fill-opacity", 0.05);
          tooltip.style("display", "none");
        });

      /* vertex circles */
      const vertices = path.values.map((v, i) => ({ value: v, index: i }));

      mainGroup
        .selectAll(null)
        .data(vertices)
        .enter()
        .append("circle")
        .attr("tabindex", 0)
        .attr("cx", (d) => radiusScale(d.value) * Math.cos(angleScale(d.index) - Math.PI / 2))
        .attr("cy", (d) => radiusScale(d.value) * Math.sin(angleScale(d.index) - Math.PI / 2))
        .attr("r", 10)
        .attr("fill", "transparent")
        .attr("pointer-events", "all")
        .on("mouseover touchstart", (event, d) => {
          radarPath.transition().duration(150).attr("fill-opacity", 0.6);
          const category = categories[d.index];
          const categoryName = category ? (category.name || `Criterion ${d.index + 1}`) : `Criterion ${d.index + 1}`;
          tooltip
            .style("display", "block")
            .html(`<i>${categoryName}:</i> ${d.value}`)
            .style("left", `${event.pageX + 5}px`)
            .style("top", `${event.pageY + 5}px`);
        })
        .on("mousemove touchmove", (event) => {
          tooltip
            .style("left", `${event.pageX + 5}px`)
            .style("top", `${event.pageY + 5}px`);
        })
        .on("mouseout touchend blur", () => {
          radarPath.transition().duration(150).attr("fill-opacity", 0.05);
          tooltip.style("display", "none");
        });
    });
		mainGroup.selectAll('circle').raise();

    /* ICONS */
    categories.forEach((d, i) => {
      const x = (radius + labelPadding) * Math.cos(angleScale(i) - Math.PI / 2);
      const y = (radius + labelPadding) * Math.sin(angleScale(i) - Math.PI / 2);
      const opacity = (d.type === 'Important' || d.category === 'Important') ? 0.4 : 1;

      if (d.emoji) {
        // Use emoji
        mainGroup
          .append("text")
          .attr("class", "category-emoji")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "central")
          .attr("font-size", `${iconSize}px`)
          .attr("opacity", opacity)
          .style("user-select", "none")
          .style("cursor", "help")
          .text(d.emoji)
          .on("mouseover touchstart", (event) => {
            tooltip
              .style("display", "block")
              .html(d.name)
              .style("left", `${event.pageX + 10}px`)
              .style("top", `${event.pageY + 10}px`);
          })
          .on("mousemove touchmove", (event) => {
            tooltip
              .style("left", `${event.pageX + 10}px`)
              .style("top", `${event.pageY + 10}px`);
          })
          .on("mouseout touchend", () => {
            tooltip.style("display", "none");
          });
      } else {
        // Use SVG icon (fallback)
        mainGroup
          .append("image")
          .attr("class", "category-icon")
          .attr("x", x - iconSize / 2)
          .attr("y", y - iconSize / 2)
          .attr("href", d.icon || iconMapping[d.name] || iconMapping.default)
          .attr("opacity", opacity)
          .attr("width", iconSize)
          .attr("height", iconSize)
          .style("cursor", "help")
          .on("mouseover touchstart", (event) => {
            tooltip
              .style("display", "block")
              .html(d.name)
              .style("left", `${event.pageX + 10}px`)
              .style("top", `${event.pageY + 10}px`);
          })
          .on("mousemove touchmove", (event) => {
            tooltip
              .style("left", `${event.pageX + 10}px`)
              .style("top", `${event.pageY + 10}px`);
          })
          .on("mouseout touchend", () => {
            tooltip.style("display", "none");
          });
      }
    });
  }

  /* ---------- LIFECYCLE ---------- */
  $effect(() => {
    tooltip = select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("padding", "5px 10px")
      .style("background", "#333")
      .style("color", "#fff")
      .style("border-radius", "5px")
      .style("pointer-events", "none")
      .style("display", "none");

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
		return() =>{
			window.removeEventListener("resize", updateDimensions);
			tooltip.remove();
		};
  });

  /* ---------- REACTIVE ---------- */
  $effect(() => {                                   // tracks deps automatically
    categories; paths; width; height;
   if (categories.length && paths.length) createRadarChart();
  });
</script>

<svg bind:this={svgElement} {width} {height} viewBox={`0 0 ${width} ${height}`}></svg>

<style>
  svg {
    width: 100%;
    height: auto;
    max-width: 800px;
    display: block;
  }
</style>
